import React, { Component } from "react";
import { Button, Form, Dropdown } from 'react-bootstrap';
import ArrowBackSharpIcon from "@material-ui/icons/ArrowBackSharp";
import axios from '../../../axios';
import apiError from '../../../error';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import DebouncedLoader from '../../../component/loader/debounced-loader';
import RightSideModalSendReportToEmails from '../../../component/rightside-modal/send-report-to-emails';
import iconSimulation from './assets/icon-simulation.png';
import iconTraining from './assets/icon-training.png';
import headerBanner from './assets/header-banner.png';
import './simulation-training-report.css';

// funnel bar + percent-badge colours (mirror the Typst report's stageColor/pctPalette)
const STAGE_COLOR = {
    sent: '#22A55B', opened: '#3B82F6', click: '#F0B429', submit: '#EF4444',
    'train-assigned': '#8B5CF6', 'train-started': '#9CA3AF', 'train-completed': '#22A55B',
};
const PCT_FG = {
    sent: '#16A34A', opened: '#3B82F6', click: '#E08A12', submit: '#DC2626',
    'train-assigned': '#8B5CF6', 'train-started': '#4B5563', 'train-completed': '#16A34A',
};
const PCT_BG = {
    sent: '#E7F6EE', opened: '#E8F0FE', click: '#FCF3DD', submit: '#FBE7E7',
    'train-assigned': '#EFEAFB', 'train-started': '#EEF0F2', 'train-completed': '#E7F6EE',
};

// Table-of-Contents sections (mirror the Typst report layout)
const reportSectionList = [
    { key: 'campaign-summary', label: 'Campaign Summary' },
    { key: 'simulation-training-funnel', label: 'Simulation to Training Funnel' },
    { key: 'key-metrics', label: 'Key Metrics' },
    { key: 'user-wise-summary', label: 'User Wise Summary' },
    { key: 'department-wise-summary', label: 'Department Wise Summary' },
    { key: 'designation-wise-summary', label: 'Designation Wise Summary' },
    { key: 'location-wise-summary', label: 'Location Wise Summary' },
    { key: 'summary-and-conclusion', label: 'Summary & Conclusion' },
];
const ALL_SECTION_KEYS = reportSectionList.map(s => s.key);

export default class SimulationTrainingReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            campaignId: parseInt(this.props.match.params.campaignId, 10),
            simulationId: parseInt(this.props.match.params.simulationId, 10),
            loading: true,
            sim: null,
            training: null,
            trainingAvailable: true,
            strings: null,
            conclusion: null,
            selectedSections: this.readCache(this.props.match.params.simulationId),
            isReportGenerating: false,
            showSendReportToEmailsModal: false,
        };
    }

    componentDidMount() {
        this.fetchData();
        this.fetchTrainingReportSections();
    }

    // ---- Table-of-Contents section selection ----
    // Source of truth is the DB (simulations.trainingExecutiveReportSectionIds),
    // fetched on mount and persisted on every toggle — mirrors reports.js. The
    // localStorage copy is ONLY a cache for fast first paint, never authoritative.
    storageKey = (simulationId) => `simTrainingReportSections:${simulationId}`;

    normalizeSections = (arr) => {
        const known = (arr || []).filter(k => ALL_SECTION_KEYS.includes(k));
        if (!known.includes('campaign-summary')) known.unshift('campaign-summary'); // mandatory
        return ALL_SECTION_KEYS.filter(k => known.includes(k)); // canonical order
    };

    readCache = (simulationId) => {
        try {
            const raw = window.localStorage.getItem(this.storageKey(simulationId));
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) return this.normalizeSections(parsed);
            }
        } catch (e) { /* ignore */ }
        return null; // no cache -> loader until the DB responds
    };

    writeCache = (sections) => {
        try { window.localStorage.setItem(this.storageKey(this.state.simulationId), JSON.stringify(sections)); } catch (e) { /* ignore */ }
    };

    // GET from DB (authoritative), overwriting the cached value
    fetchTrainingReportSections = async () => {
        try {
            const res = await axios.get(`/api/pdfReport/get-training-report-sections/${this.state.simulationId}`);
            const sections = this.normalizeSections(res.data.trainingExecutiveReportSectionIds || []);
            this.setState({ selectedSections: sections });
            this.writeCache(sections);
        } catch (err) {
            apiError(err);
        }
    };

    // POST to DB (persist), then reflect in state + cache — like reports.js
    updateTrainingReportSection = async (updatedIds) => {
        const sections = this.normalizeSections(updatedIds);
        try {
            await axios.post(`/api/pdfReport/update-training-report-sections/${this.state.simulationId}`, {
                trainingExecutiveReportSectionIds: sections,
            });
            this.setState({ selectedSections: sections });
            this.writeCache(sections);
        } catch (err) {
            apiError(err);
        }
    };

    fetchData = async () => {
        try {
            this.setState({ loading: true });
            const res = await axios.get(`/api/pdfReport/simulation-training-report/${this.state.simulationId}`);
            this.setState({
                sim: res.data.simulation,
                training: res.data.training,
                strings: res.data.strings,
                conclusion: res.data.conclusion,
                // false when the LMS training data could not be fetched -> render "NA"
                trainingAvailable: res.data.trainingAvailable !== false,
                loading: false,
            });
        } catch (err) {
            this.setState({ loading: false });
            apiError(err);
        }
    };

    tr = (key) => (this.state.strings && this.state.strings.ui && this.state.strings.ui[key]) || key;
    trEnum = (section, key) => {
        const s = this.state.strings;
        if (s && s.enums && s.enums[section] && s.enums[section][key] != null) return s.enums[section][key];
        return key;
    };

    showSection = (key) => Array.isArray(this.state.selectedSections) && this.state.selectedSections.includes(key);

    toggleSection = (key, checked) => {
        if (key === 'campaign-summary') return; // mandatory, always included
        const current = this.state.selectedSections || [];
        let updated;
        if (checked) {
            updated = current.includes(key) ? current : [...current, key];
        } else {
            updated = current.filter(k => k !== key);
        }
        // persist to DB (source of truth); state + cache update on success
        this.updateTrainingReportSection(updated);
    };

    downloadPdf = async (languageCode = 'en') => {
        if (this.state.isReportGenerating) return;
        try {
            this.setState({ isReportGenerating: true });
            const res = await axios.get(`/api/pdfReport/simulation-training-report/${this.state.simulationId}/download`, {
                params: { languageCode, sections: (this.state.selectedSections || []).join(',') },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `simulation-training-report-${languageCode}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log(err);
            toast.error("Failed to generate the report. Please try again.");
        } finally {
            this.setState({ isReportGenerating: false });
        }
    };

    sendReport = async (emails) => {
        try {
            this.setState({ isReportGenerating: true });
            await axios.post(`/api/pdfReport/simulation-training-report/${this.state.simulationId}/send`, {
                emails,
                languageCode: 'en',
                sections: this.state.selectedSections || [],
            });
            toast.success("Report sent to emails successfully");
        } catch (err) {
            apiError(err);
        } finally {
            this.setState({ isReportGenerating: false, showSendReportToEmailsModal: false });
        }
    };

    // ---- section renderers ----
    kvRow = (label, value) => (
        <div className="st-kv-row" key={label}>
            <div className="st-kv-label">{label}</div>
            <div className="st-kv-value">{value}</div>
        </div>
    );

    // Campaign-summary value for a list of names (templates / courses).
    //   0 -> "---"      1 -> "the name"
    //   n -> bold count + muted "(first, second + N more)" — same shape as Total Users.
    //   Mirrors nameList() in report.typ.
    nameList = (items) => {
        const list = (items || []).filter(Boolean);
        if (!list.length) return '---';
        if (list.length === 1) return list[0];
        const head = Math.min(2, list.length);
        const rest = list.length - head;
        const more = rest > 0 ? ` ${this.tr('n-more').replace('{n}', rest)}` : '';
        return <span><b>{list.length}</b> <span className="st-muted">({list.slice(0, head).join(', ')}{more})</span></span>;
    };

    renderCampaignSummary() {
        const { sim, training, trainingAvailable } = this.state;
        const s = sim.simulation;
        const t = training.training;
        const na = <span className="st-na">{this.tr('na')}</span>;
        return (
            <div className="st-section" key="campaign-summary">
                <h3 className="st-section-title">{this.tr('campaign-summary')}</h3>
                <div className="row">
                    <div className="col-md-6 st-col">
                        <div className="st-col-head"><img src={iconSimulation} className="st-col-icon" alt="" /> {this.tr('simulation-tsat')}</div>
                        <div className="st-col-rule" />
                        {this.kvRow(this.tr('campaign-name'), s.campaignName)}
                        {this.kvRow(this.tr('attack-vector'), <span className="st-tag st-tag-purple">{this.trEnum('attackVector', s.attackVector)}</span>)}
                        {this.kvRow(this.tr('duration'), s.duration)}
                        {this.kvRow(this.tr('total-users'), s.isFollowUpCampaign
                            ? <b>{s.totalUsers}</b>
                            : <span><b>{s.totalUsers}</b> <span className="st-muted">({s.groupsCount} {this.tr('groups')})</span></span>)}
                        {this.kvRow(this.tr('credential-harvesting'), this.trEnum('credential', s.credentialHarvesting ? 'On' : 'Off'))}
                        {this.kvRow(this.tr('template-used'), this.nameList(s.templatesUsed))}
                        {this.kvRow(this.tr('status'), <span className="st-status-green">{this.trEnum('status', s.status)}</span>)}
                    </div>
                    <div className="col-md-6 st-col">
                        <div className="st-col-head"><img src={iconTraining} className="st-col-icon" alt="" /> {this.tr('training-tlms')}</div>
                        <div className="st-col-rule" />
                        {this.kvRow(this.tr('course-assigned'), trainingAvailable ? this.nameList(t.coursesAssigned) : na)}
                        {/* trigger is the awareness-targeted group; null = no trigger configured -> NA */}
                        {this.kvRow(this.tr('trigger'), (trainingAvailable && t.trigger) ? <span className="st-tag">{this.trEnum('trigger', t.trigger)}</span> : na)}
                        {this.kvRow(this.tr('assigned-on'), trainingAvailable ? this.trEnum('assignedOn', t.assignedOn) : na)}
                        {this.kvRow(this.tr('user-enrolled'), trainingAvailable ? <b>{t.userEnrolled}</b> : na)}
                        {this.kvRow(this.tr('expires-on'), trainingAvailable ? t.expiresOn : na)}
                        {this.kvRow(this.tr('status'), trainingAvailable ? <span className="st-status-blue">{this.trEnum('status', t.status)}</span> : na)}
                    </div>
                </div>
            </div>
        );
    }

    renderFunnel() {
        const { sim, training } = this.state;
        const simStages = sim.simStages || [];
        const trainStages = training.trainFunnel || [];
        const all = [...simStages, ...trainStages];
        const maxVal = Math.max(1, ...all.map(x => x.value || 0));
        const simN = simStages.length;
        const column = (stage, idx) => {
            const isNa = stage.value === null || stage.value === undefined;
            const h = isNa ? 30 : 30 + ((stage.value || 0) / maxVal) * 120;
            return (
                <div className="st-funnel-col" key={idx}>
                    <div className="st-funnel-bar-area">
                        <div className="st-funnel-bar" style={{ height: `${h}px`, background: isNa ? '#E4E7EC' : (STAGE_COLOR[stage.color] || '#3B82F6') }} />
                    </div>
                    <div className="st-funnel-val" style={isNa ? { color: '#9CA3AF' } : {}}>{isNa ? this.tr('na') : stage.value}</div>
                    <div className="st-funnel-label">{this.tr(stage.labelKey)}</div>
                    <div className="st-funnel-pct" style={isNa ? { color: '#9CA3AF', background: '#EEF0F2' } : { color: PCT_FG[stage.color] || '#3B82F6', background: PCT_BG[stage.color] || '#E8F0FE' }}>{isNa ? this.tr('na') : `${stage.pct}%`}</div>
                </div>
            );
        };
        return (
            <div className="st-section" key="simulation-training-funnel">
                <h3 className="st-section-title">{this.tr('funnel-title')}</h3>
                <div className="st-funnel-box">
                    <div className="st-funnel-row">
                        {all.map((stage, i) => (
                            <React.Fragment key={i}>
                                {i === simN && simN > 0 && (
                                    <div className="st-funnel-divider">
                                        <div className="st-auto-assigned">{this.tr('auto-assigned')}</div>
                                        <div className="st-dashed" />
                                    </div>
                                )}
                                {column(stage, i)}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="st-funnel-pills">
                        <span className="st-pill">{this.tr('pill-simulation')}</span>
                        <span className="st-pill">{this.tr('pill-training')}</span>
                    </div>
                </div>
            </div>
        );
    }

    renderKeyMetrics() {
        const { sim, training, trainingAvailable } = this.state;
        const m = sim.metrics;
        const rem = training.remediation || { pct: 0, completed: 0, total: 0 };
        return (
            <div className="st-section" key="key-metrics">
                <h3 className="st-section-title">{this.tr('key-metrics')}</h3>
                <div className="row">
                    <div className="col-md-4">
                        <div className="st-metric-card">
                            <div className="st-metric-label">{this.tr(m.riskRateLabel).toUpperCase()}</div>
                            <div className="st-metric-value st-red">{m.phishRiskRate}%</div>
                            <div className="st-metric-sub">{this.tr('industry-avg')} {m.industryAvg}%: <b className="st-red">{this.trEnum('riskLevel', m.riskLevel)}</b></div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="st-metric-card">
                            <div className="st-metric-label">{this.tr('average-evs').toUpperCase()}</div>
                            <div className="st-metric-value st-orange">{m.averageEvs}%</div>
                            <div className="st-metric-sub">{this.tr(sim.labels.minTime)} <b>{m.minTime}</b></div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="st-metric-card">
                            <div className="st-metric-label">{this.tr('training-completion').toUpperCase()}</div>
                            <div className={trainingAvailable ? "st-metric-value st-green" : "st-metric-value st-na"}>{trainingAvailable ? `${training.trainingCompletion}%` : this.tr('na')}</div>
                            <div className="st-metric-sub">{this.tr('avg-completion')}</div>
                        </div>
                    </div>
                </div>
                <div className="st-remediation">
                    <div className="st-remediation-left">
                        <div className="st-remediation-title">{this.tr('risk-remediation-rate')}</div>
                        <div className="st-muted">{this.tr('risk-remediation-desc1')}</div>
                        <div className="st-muted">{this.tr('risk-remediation-desc2')}</div>
                    </div>
                    <div className="st-remediation-right">
                        {trainingAvailable ? (
                            <React.Fragment>
                                <div className="st-remediation-pct">{rem.pct}%</div>
                                <div className="st-progress-track">
                                    <div className="st-progress-fill" style={{ width: `${Math.max(0, Math.min(100, rem.pct))}%` }} />
                                </div>
                                <div className="st-muted st-right">({rem.completed} {this.tr('conclusion-mid')} {rem.total}) {this.tr('users')}</div>
                            </React.Fragment>
                        ) : (
                            <div className="st-remediation-pct st-na st-right">{this.tr('na')}</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    trainBar = (pct) => (
        <div className="st-trainbar">
            <div className="st-trainbar-track"><div className="st-trainbar-fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
            <span className="st-trainbar-label">{pct}%</span>
        </div>
    );

    behaviourColor = (b) => {
        if (b === 'Link Clicked') return 'st-orange';
        if (['Data Submitted', 'System Hacked', 'Attachment Opened'].includes(b)) return 'st-red';
        return 'st-muted';
    };

    renderUserSummary() {
        const { sim, training, trainingAvailable } = this.state;
        const users = sim.userSummary || [];
        const perUser = training.perUserTraining || {};
        const na = <span className="st-na">{this.tr('na')}</span>;
        return (
            <div className="st-section" key="user-wise-summary">
                <h3 className="st-section-title">{this.tr('risk-remediation-summary')}</h3>
                <div className="st-card">
                    <div className="st-card-head">{this.tr('user-summary')} <span className="st-muted">{this.tr('user-summary-note')}</span></div>
                    <table className="st-table">
                        <thead>
                            <tr>
                                <th>{this.tr('col-name')}</th>
                                <th>{this.tr('col-designation')}</th>
                                <th>{this.tr('col-simulation-behaviour')}</th>
                                <th>{this.tr(sim.labels.userTime)}</th>
                                <th>{this.tr('col-evs')}</th>
                                <th>{this.tr('col-training')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u, i) => {
                                const training_pct = perUser[u.email] != null ? perUser[u.email] : training.defaultUserTraining;
                                return (
                                    <tr key={i}>
                                        <td><div className="st-user-name">{u.name}</div><div className="st-user-email">{u.email}</div></td>
                                        <td>{u.designation}</td>
                                        <td className={this.behaviourColor(u.behaviour)}>{this.trEnum('behaviour', u.behaviour)}</td>
                                        <td className="st-red">{u.breachTime}</td>
                                        <td className="st-red"><b>{u.evs}%</b></td>
                                        <td>{trainingAvailable ? this.trBar(training_pct) : na}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    trBar = (pct) => this.trainBar(pct);

    renderGroupTable(sectionKey, title, rows) {
        const { sim, trainingAvailable } = this.state;
        // Rows arrive already merged with the LMS training data by the backend
        // (simulationTrainingReport/enrichGroups.js) — the same helper the downloaded
        // PDF uses, so the preview and the PDF cannot disagree. Each row carries
        // enrolled, training (%) and trainingNotRequired.
        const na = <span className="st-na">{this.tr('na')}</span>;
        const notRequired = <span className="st-not-required">{this.tr('not-required')}</span>;
        return (
            <div className="st-section" key={sectionKey}>
                <div className="st-card">
                    <div className="st-card-head">{title}</div>
                    <table className="st-table">
                        <thead>
                            <tr>
                                <th>{title.replace(' Summary', '')}</th>
                                <th>{this.tr(sim.labels.sentCol)}</th>
                                <th>{this.tr(sim.labels.compromisedCol)}</th>
                                <th>{this.tr('col-enrolled')}</th>
                                <th>{this.tr('col-evs')}</th>
                                <th>{this.tr('col-training')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(rows || []).map((r, i) => {
                                let trainingCell;
                                if (!trainingAvailable) trainingCell = na;
                                else if (r.trainingNotRequired) trainingCell = notRequired;
                                else trainingCell = this.trainBar(r.training);
                                return (
                                    <tr key={i}>
                                        <td>{r.label}</td>
                                        <td>{r.emailsSent}</td>
                                        <td>{r.compromised}</td>
                                        <td>{r.enrolled}</td>
                                        <td className="st-red"><b>{r.evs}%</b></td>
                                        <td>{trainingCell}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderConclusion() {
        // completion-based summary + recommendations, resolved by the backend
        // (buildConclusion) so the preview matches the downloaded PDF exactly.
        const { trainingAvailable } = this.state;
        const c = this.state.conclusion || {};
        if (!trainingAvailable || c.unavailable) {
            // No training data -> nothing to conclude about remediation.
            return (
                <div className="st-section" key="summary-and-conclusion">
                    <h3 className="st-section-title">{this.tr('summary-conclusion')}</h3>
                    <div className="st-alert st-alert-na">{this.tr('training-unavailable')}</div>
                </div>
            );
        }
        return (
            <div className="st-section" key="summary-and-conclusion">
                <h3 className="st-section-title">{this.tr('summary-conclusion')}</h3>
                <div className={`st-alert st-alert-${c.caseKey || 'case1'}`}>
                    <b>{c.pendingCount}</b> {c.mid} <b>{c.totalCompromised}</b> {c.body} {c.tail}
                </div>
                <ul className="st-reco-list">
                    {(c.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
            </div>
        );
    }

    render() {
        const { sim, training, strings, loading, selectedSections } = this.state;
        const ready = sim && training && strings && Array.isArray(selectedSections);
        return (
            <div className="content-wrap report-page-wrap st-report">
                <div className="breadcrumbbar">
                    <div className="flexRow row">
                        <div className="col-md-5">
                            <div className="page-head d-flex align-items-center justify-content-between">
                                <h4 className="page-title">
                                    <a onClick={e => this.props.history.goBack()} className="bar_Icons mr-2"><ArrowBackSharpIcon className="btn-icon" /></a>
                                    Campaign Report (Simulation + Training)
                                </h4>
                            </div>
                        </div>
                        <div className="col-md-7 flexFilterButtons">
                            <Button variant="success" className="mr-2 executive-report-btn" disabled={!ready || this.state.isReportGenerating}
                                onClick={() => { if (ready) this.setState({ showSendReportToEmailsModal: true }); }}>
                                Send
                            </Button>
                            <Dropdown>
                                <Dropdown.Toggle variant="primary" disabled={!ready || this.state.isReportGenerating}>
                                    {this.state.isReportGenerating ? <Spinner loading={true}></Spinner> : "Download"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ fontSize: '12px' }}>
                                    <Dropdown.Item onClick={() => this.downloadPdf('en')}>English Report</Dropdown.Item>
                                    <Dropdown.Item onClick={() => this.downloadPdf('ar')}>Arabic Report</Dropdown.Item>
                                    <Dropdown.Item onClick={() => this.downloadPdf('id')}>Bahasa Report</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                <div className="row m-0 report-page-body">
                    <div className="col-md-3 addBorderRight">
                        <div className="tableOfContentsSidebar">
                            <h5><strong>Table of Contents</strong></h5>
                            <p>Select the content to be added in the report:</p>
                            <div className="tableOfContentsForm">
                                <Form>
                                    <Form.Group>
                                        <div>
                                            {reportSectionList.map(section => (
                                                <Form.Check
                                                    key={section.key}
                                                    type="checkbox"
                                                    id={section.key}
                                                    label={section.label}
                                                    checked={this.showSection(section.key)}
                                                    onChange={e => this.toggleSection(section.key, e.target.checked)}
                                                    disabled={!ready || section.key === 'campaign-summary'}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Form>
                            </div>
                            <div className="badge-note"><b>Note:</b> Only selected Table of Content sections appear in the preview and the downloaded/emailed report. Your selection is saved for this report.</div>
                        </div>
                    </div>

                    <div className="col-md-9 p-0 report-preview-scroll">
                        <div className="exeReportWrapper">
                            {ready && (
                                <div id="pdf-wrap" className="st-preview">
                                    {/* <img src={headerBanner} className="st-header-banner" alt="Simulation + Training — Combined Executive Summary Report" /> */}
                                    {this.showSection('campaign-summary') && this.renderCampaignSummary()}
                                    {this.showSection('simulation-training-funnel') && this.renderFunnel()}
                                    {this.showSection('key-metrics') && this.renderKeyMetrics()}
                                    {this.showSection('user-wise-summary') && this.renderUserSummary()}
                                    {this.showSection('department-wise-summary') && this.renderGroupTable('department-wise-summary', this.tr('department-summary'), sim.departmentSummary)}
                                    {this.showSection('designation-wise-summary') && this.renderGroupTable('designation-wise-summary', this.tr('designation-summary'), sim.designationSummary)}
                                    {this.showSection('location-wise-summary') && this.renderGroupTable('location-wise-summary', this.tr('location-summary'), sim.locationSummary)}
                                    {this.showSection('summary-and-conclusion') && this.renderConclusion()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <RightSideModalSendReportToEmails
                    show={this.state.showSendReportToEmailsModal}
                    handleModal={(status) => this.setState({ showSendReportToEmailsModal: status })}
                    sendReportToEmails={async (emails) => { await this.sendReport(emails); }}
                    isReportGenerating={this.state.isReportGenerating}
                />

                <DebouncedLoader loading={loading} />
            </div>
        );
    }
}
